import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import Layout from '../components/layout/Layout';
import { useSiteContent } from '../context/SiteContentContext';
import {
  ArrowRight,
  Phone,
  Users,
  Network,
  Shield,
  Cloud,
  LineChart,
  Zap,
  Award,
  Headphones,
  Play,
  CheckCircle2,
  Quote,
  Calendar
} from 'lucide-react';

const iconMap = {
  Users: Users,
  Network: Network,
  Shield: Shield,
  Cloud: Cloud,
  LineChart: LineChart,
  Zap: Zap,
  Award: Award,
  HeadphonesIcon: Headphones
};

const HomePage = () => {
  const { siteContent } = useSiteContent();
  const companyInfo = siteContent.companyInfo;
  const services = siteContent.services;
  const differentials = siteContent.differentials;
  const testimonials = siteContent.testimonials;
  const blogPosts = siteContent.blogPosts;
  const faqs = siteContent.faqs;
  const images = siteContent.images;
  const institutionalVideo = siteContent.institutionalVideo || {};
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  const videoEmbed = useMemo(() => {
    const url = (institutionalVideo.videoUrl || "").trim();
    if (!url) return null;
    try {
      const u = new URL(url);
      const host = u.hostname.replace(/^www\./, "");

      if (host === "youtu.be") {
        const id = u.pathname.replace("/", "");
        return { kind: "youtube", src: `https://www.youtube.com/embed/${id}?autoplay=1&rel=0` };
      }
      if (host === "youtube.com" || host === "m.youtube.com") {
        const id = u.searchParams.get("v");
        if (id) return { kind: "youtube", src: `https://www.youtube.com/embed/${id}?autoplay=1&rel=0` };
      }
      if (host === "vimeo.com") {
        const id = u.pathname.split("/").filter(Boolean)[0];
        if (id) return { kind: "vimeo", src: `https://player.vimeo.com/video/${id}?autoplay=1` };
      }

      return { kind: "video", src: url };
    } catch {
      return { kind: "video", src: url };
    }
  }, [institutionalVideo.videoUrl]);
  const handleWhatsApp = () => {
    window.open(
      `https://wa.me/${companyInfo.whatsapp}?text=${encodeURIComponent(companyInfo.whatsappMessage)}`,
      '_blank'
    );
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: `url(${images.hero})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          />
          {/* Decorative Elements */}
          <div className="absolute top-1/4 left-10 w-72 h-72 bg-orange-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/10">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                <span className="text-white/80 text-sm">Atendendo MA, PI e SP</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
                Sua tecnologia em
                <span className="block text-orange-500">boas mãos, sempre.</span>
              </h1>
              
              <p className="text-xl text-slate-300 max-w-lg leading-relaxed">
                Soluções completas em TI para empresas que não podem parar. 
                Suporte, cloud, segurança e infraestrutura com atendimento ágil e especializado.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={handleWhatsApp}
                  size="lg"
                  className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-6 text-lg shadow-xl shadow-orange-500/25 hover:shadow-orange-500/40 transition-all duration-300"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Fale com um especialista
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  asChild
                  className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg"
                >
                  <Link to="/servicos">
                    Nossos serviços
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Link>
                </Button>
              </div>

              {/* Trust Badges */}
              <div className="flex items-center gap-8 pt-8 border-t border-white/10">
                <div className="text-center">
                  <p className="text-3xl font-bold text-white">500+</p>
                  <p className="text-sm text-slate-400">Clientes atendidos</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-white">10+</p>
                  <p className="text-sm text-slate-400">Anos de experiência</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-white">24/7</p>
                  <p className="text-sm text-slate-400">Suporte disponível</p>
                </div>
              </div>
            </div>

            {/* Hero Image/Card */}
            <div className="hidden lg:block relative">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-orange-500/20 to-blue-500/20 rounded-3xl blur-2xl" />
                <div className="relative bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 p-8">
                  <img
                    src={images.services}
                    alt="Profissionais de TI"
                    className="rounded-2xl w-full h-80 object-cover"
                  />
                  {/* Floating Cards */}
                  <div className="absolute -left-8 top-1/4 bg-white rounded-xl shadow-2xl p-4 animate-float">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-800">Chamado #1234</p>
                        <p className="text-xs text-green-600">Resolvido em 15min</p>
                      </div>
                    </div>
                  </div>
                  <div className="absolute -right-8 bottom-1/4 bg-white rounded-xl shadow-2xl p-4 animate-float-delayed">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                        <Shield className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-800">Sistemas Protegidos</p>
                        <p className="text-xs text-slate-500">99.9% uptime</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 rounded-full bg-orange-100 text-orange-600 text-sm font-medium mb-4">
              Conheça a DRSUPORTI
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
              Como a DRSUPORTI constrói soluções em TI
            </h2>
            <p className="text-lg text-slate-600 max-w-none mx-auto whitespace-nowrap">
              Descubra como nossa equipe trabalha para manter sua empresa sempre operando
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-slate-900 aspect-video">
              {/* Video Placeholder - Ready for YouTube/Vimeo embed */}
              {!isVideoPlaying && (
              <div className="absolute inset-0 flex items-center justify-center">
                <img
                  src={institutionalVideo.coverUrl || images.about}
                  alt="Vídeo institucional DRSUPORTI"
                  className="w-full h-full object-cover opacity-50"
                />
                <button
                  onClick={() => {
                    if (videoEmbed) setIsVideoPlaying(true);
                  }}
                  className={`absolute w-20 h-20 rounded-full flex items-center justify-center shadow-xl transition-all duration-300 group ${
                    videoEmbed
                      ? "bg-orange-500 hover:bg-orange-600 shadow-orange-500/30 hover:scale-110"
                      : "bg-slate-600 cursor-not-allowed opacity-70"
                  }`}
                >
                  <Play className="w-8 h-8 text-white ml-1 group-hover:scale-110 transition-transform" />
                </button>
              </div>
              )}
              {isVideoPlaying && (
                <div className="absolute inset-0 bg-black">
                  {videoEmbed?.kind === "video" ? (
                    <video
                      src={videoEmbed.src}
                      controls
                      autoPlay
                      playsInline
                      poster={institutionalVideo.coverUrl || images.about}
                      className="w-full h-full"
                    />
                  ) : (
                    <iframe
                      title="Vídeo institucional"
                      src={videoEmbed?.src || ""}
                      className="w-full h-full"
                      allow="autoplay; encrypted-media; picture-in-picture"
                      allowFullScreen
                    />
                  )}
                </div>
              )}
              {/* Video info overlay */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                <p className="text-white font-medium">Vídeo Institucional</p>
                <p className="text-white/60 text-sm">Duração: 1:30</p>
              </div>
            </div>

            <div className="text-center mt-8">
              <Button
                onClick={handleWhatsApp}
                className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-6 text-lg shadow-lg shadow-orange-500/25"
              >
                <Phone className="w-5 h-5 mr-2" />
                Quero conhecer as soluções
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mb-4">
              Nossos Serviços
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
              Soluções completas para sua empresa
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Oferecemos uma gama completa de serviços de TI para manter sua operação funcionando sem interrupções
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Card
                  key={service.id}
                  className="group border-0 shadow-lg shadow-slate-200/50 hover:shadow-xl hover:shadow-orange-100 transition-all duration-300 hover:-translate-y-1"
                >
                  <CardContent className="p-8">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                      {IconComponent && <IconComponent className="w-7 h-7 text-white" />}
                    </div>
                    <h3 className="text-xl font-bold text-slate-800 mb-3">
                      {service.title}
                    </h3>
                    <p className="text-slate-600 mb-6 leading-relaxed">
                      {service.description}
                    </p>
                    <ul className="space-y-2">
                      {service.features.slice(0, 3).map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm text-slate-500">
                          <CheckCircle2 className="w-4 h-4 text-orange-500 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Button
              asChild
              variant="outline"
              size="lg"
              className="border-slate-300 text-slate-700 hover:bg-slate-50 px-8"
            >
              <Link to="/servicos">
                Ver todos os serviços
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Differentials Section */}
      <section className="py-24 bg-slate-900 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 rounded-full bg-orange-500/20 text-orange-400 text-sm font-medium mb-4">
              Por que nos escolher
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Nossos Diferenciais
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              O que faz a DRSUPORTI ser a escolha certa para sua empresa
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {differentials.map((diff) => {
              const IconComponent = iconMap[diff.icon];
              return (
                <div
                  key={diff.id}
                  className="group text-center p-8 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 transition-all duration-300"
                >
                  <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    {IconComponent && <IconComponent className="w-8 h-8 text-white" />}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">
                    {diff.title}
                  </h3>
                  <p className="text-slate-400 leading-relaxed">
                    {diff.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mb-4">
              Depoimentos
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
              O que nossos clientes dizem
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card
                key={testimonial.id}
                className="border-0 shadow-lg shadow-slate-200/50"
              >
                <CardContent className="p-8">
                  <Quote className="w-10 h-10 text-orange-200 mb-4" />
                  <p className="text-slate-600 mb-6 leading-relaxed">
                    "{testimonial.content}"
                  </p>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center">
                      {testimonial.photo ? (
                        <img
                          src={testimonial.photo}
                          alt={testimonial.name}
                          className="w-full h-full rounded-full object-cover"
                        />
                      ) : (
                        <span className="text-white font-bold">
                          {testimonial.name.charAt(0)}
                        </span>
                      )}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-800">{testimonial.name}</p>
                      <p className="text-sm text-slate-500">
                        {testimonial.role} - {testimonial.company}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Section */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <span className="inline-block px-4 py-2 rounded-full bg-orange-100 text-orange-600 text-sm font-medium mb-4">
                Blog
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800">
                Últimas notícias e artigos
              </h2>
            </div>
            <Button
              asChild
              variant="outline"
              className="mt-4 md:mt-0 border-slate-300"
            >
              <Link to="/blog">
                Ver todos os artigos
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {blogPosts.map((post) => (
              <Card
                key={post.id}
                className="group border-0 shadow-lg shadow-slate-200/50 overflow-hidden hover:shadow-xl transition-all duration-300"
              >
                <div className="aspect-video overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <span className="px-3 py-1 rounded-full bg-orange-100 text-orange-600 text-xs font-medium">
                      {post.category}
                    </span>
                    <span className="flex items-center gap-1 text-xs text-slate-500">
                      <Calendar className="w-3 h-3" />
                      {new Date(post.date).toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-slate-800 mb-2 group-hover:text-orange-500 transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {post.excerpt}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mb-4">
              FAQ
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
              Perguntas Frequentes
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="border border-slate-200 rounded-xl px-6 data-[state=open]:bg-orange-50 data-[state=open]:border-orange-200 transition-colors"
              >
                <AccordionTrigger className="text-left font-semibold text-slate-800 hover:text-orange-500 hover:no-underline py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-slate-900 to-slate-800 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-10">
          <img
            src={images.network}
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/95 to-transparent" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Pronto para ter sua tecnologia em boas mãos?
            </h2>
            <p className="text-xl text-slate-300 mb-8">
              Entre em contato agora e descubra como podemos ajudar sua empresa a crescer com segurança e eficiência.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={handleWhatsApp}
                size="lg"
                className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-6 text-lg shadow-xl shadow-orange-500/25"
              >
                <Phone className="w-5 h-5 mr-2" />
                Fale com um especialista
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg"
              >
                <Link to="/contato">
                  Ver outras formas de contato
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default HomePage;
